import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { Eye, EyeOff, Lock, User, AlertCircle, Clock } from 'lucide-react'
import clsx from 'clsx'

export default function Login() {
  const navigate = useNavigate()
  const { login, isLoading, error, rateLimitStatus, clearError, isAuthenticated } = useAuthStore()
  
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState({ userName: false, password: false })
  const [isMobile, setIsMobile] = useState(false)
  
  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])
  
  // Clear error when inputs change
  useEffect(() => {
    if (error) {
      clearError()
    }
  }, [userName, password])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!userName.trim() || !password) {
      setTouched({ userName: true, password: true })
      return
    }
    
    const success = await login(userName.trim(), password)
    
    if (success) {
      navigate('/', { replace: true })
    }
  }
  
  const isBlocked = rateLimitStatus?.blocked
  const remainingAttempts = rateLimitStatus?.remainingAttempts ?? null
  
  // Format blocked until time
  const formatBlockedUntil = (blockedUntil: string | null) => {
    if (!blockedUntil) return ''
    
    const date = new Date(blockedUntil)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffMins = Math.ceil(diffMs / 60000)
    
    if (diffMins <= 1) return 'menos de 1 minuto'
    if (diffMins < 60) return `${diffMins} minutos`
    
    return `${Math.ceil(diffMins / 60)} hora(s)`
  }
  
  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Background image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${isMobile ? '/background_mobile.png' : '/background_desktop.png'})` 
        }}
      />
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/30" />
      
      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 relative z-10">
        {/* Logo and title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-lg mb-4 p-3">
            <img src="/logo.svg" alt="Xafra Ads Logo" className="w-full h-full object-contain" />
          </div>
          <p className="text-white/90 text-sm md:text-base drop-shadow">
            Business Intelligence Dashboard
          </p>
        </div>
        
        {/* Login card */}
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              Iniciar Sesión
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error alert */}
              {error && (
                <div className={clsx(
                  'flex items-start gap-3 p-3 rounded-lg text-sm',
                  isBlocked 
                    ? 'bg-error-50 text-error-600' 
                    : 'bg-warning-50 text-warning-600'
                )}>
                  {isBlocked ? (
                    <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{error}</p>
                    {isBlocked && rateLimitStatus?.blockedUntil && (
                      <p className="mt-1 text-xs opacity-80">
                        Podrá intentar nuevamente en {formatBlockedUntil(rateLimitStatus.blockedUntil)}
                      </p>
                    )}
                    {!isBlocked && remainingAttempts !== null && remainingAttempts <= 5 && (
                      <p className="mt-1 text-xs opacity-80">
                        Intentos restantes: {remainingAttempts}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Username field */}
              <div>
                <label htmlFor="userName" className="label">
                  Usuario
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="userName"
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    onBlur={() => setTouched(t => ({ ...t, userName: true }))}
                    className={clsx(
                      'input pl-10',
                      touched.userName && !userName.trim() && 'input-error'
                    )}
                    placeholder="Nombre de usuario"
                    disabled={isLoading || isBlocked}
                    autoComplete="username"
                    autoFocus
                  />
                </div>
                {touched.userName && !userName.trim() && (
                  <p className="mt-1 text-xs text-error-500">El usuario es requerido</p>
                )}
              </div>
              
              {/* Password field */}
              <div>
                <label htmlFor="password" className="label">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched(t => ({ ...t, password: true }))}
                    className={clsx(
                      'input pl-10 pr-10',
                      touched.password && !password && 'input-error'
                    )}
                    placeholder="Contraseña"
                    disabled={isLoading || isBlocked}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {touched.password && !password && (
                  <p className="mt-1 text-xs text-error-500">La contraseña es requerida</p>
                )}
              </div>
              
              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading || isBlocked}
                className="btn-primary w-full py-3 mt-6"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Iniciando sesión...
                  </span>
                ) : isBlocked ? (
                  <span className="flex items-center justify-center gap-2">
                    <Clock className="w-5 h-5" />
                    Cuenta bloqueada
                  </span>
                ) : (
                  'Iniciar Sesión'
                )}
              </button>
            </form>
          </div>
          
          {/* Security info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-white/80 drop-shadow">
              Conexión segura • Máximo 10 intentos por hora
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="py-4 text-center relative z-10">
        <p className="text-xs text-white/70 drop-shadow">
          © {new Date().getFullYear()} Xafra Ads • v5.0
        </p>
      </footer>
    </div>
  )
}
